import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {agreeToOpenBank} from '@web/actions/agreementInfo'
import {getBankEasyPays} from '@web/actions/paymentMethod'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {APIStatus} from '@web/core/constants'
import {
    agreeToBookingAgreed,
    agreeToCustomOrderAgreed,
    agreeToFamilyPayAgreed,
    agreeToFamilyPayMinorAgreed,
    agreeToIiomaAgreed,
    agreeToMallContractAgreed,
    agreeToOpenBanking,
    agreeToPreOrderAgreed,
    agreeToShoppingSubscriptionAgreed,
    agreeToThirdPeopleAgreed,
    agreeToReplaceExistingPaymentMethod,
    enableAllAgreement,
    getInitialState,
    AgreementState,
} from '@web/slices/agreement/slice'

import type {AgreementReducers, MakeCreateSliceOptions} from './interfaces'

export default (options?: MakeCreateSliceOptions) =>
    createSlice<AgreementState, AgreementReducers, 'agreement', SliceSelectors<AgreementState>>({
        name: 'agreement',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(enableAllAgreement, (state, {payload}) => {
                    state.enabled = payload.enabled
                    state.selected = payload.selected
                })
                .addCase(getBankEasyPays.fulfilled, (state, {payload}) => {
                    if (payload.isRegistered) {
                        state.openBankingAgreed = true
                    }
                })
                .addCase(agreeToFamilyPayMinorAgreed, (state, {payload}) => {
                    state.familyPayMinorAgreed = payload
                })
                .addCase(agreeToReplaceExistingPaymentMethod, (state, {payload}) => {
                    state.replaceExistingPaymentMethod = payload
                })
                .addCase(agreeToFamilyPayAgreed, (state, {payload}) => {
                    state.selected.familyPay = payload
                })
                .addCase(agreeToMallContractAgreed, (state, {payload}) => {
                    state.selected.mallContract = payload
                })
                .addCase(agreeToThirdPeopleAgreed, (state, {payload}) => {
                    state.selected.thirdPeople = payload
                })
                .addCase(agreeToIiomaAgreed, (state, {payload}) => {
                    state.selected.personalDataCrossBorderTransfer = payload
                })
                .addCase(agreeToCustomOrderAgreed, (state, {payload}) => {
                    state.selected.customOrder = payload
                })
                .addCase(agreeToPreOrderAgreed, (state, {payload}) => {
                    state.selected.preOrder = payload
                })
                .addCase(agreeToBookingAgreed, (state, {payload}) => {
                    state.selected.booking = payload
                })
                .addCase(agreeToShoppingSubscriptionAgreed, (state, {payload}) => {
                    state.selected.shoppingSubscription = payload
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(isAnyOf(agreeToOpenBank.pending), (state) => {
                    state.openBankingAgreeApiStatus = APIStatus.FETCHING
                })
                .addMatcher(isAnyOf(agreeToOpenBanking, agreeToOpenBank.fulfilled), (state, {payload}) => {
                    state.openBankingAgreed = payload
                    state.openBankingAgreeApiStatus = APIStatus.RESOLVED
                })
                .addMatcher(isAnyOf(agreeToOpenBank.rejected), (state) => {
                    state.openBankingAgreed = true
                    state.openBankingAgreeApiStatus = APIStatus.REJECTED
                })
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.agreement ?? state
                })
                .addMatcher(
                    isAnyOf(
                        agreeToMallContractAgreed,
                        agreeToThirdPeopleAgreed,
                        agreeToIiomaAgreed,
                        agreeToCustomOrderAgreed,
                        agreeToPreOrderAgreed,
                        agreeToFamilyPayAgreed,
                        agreeToBookingAgreed,
                    ),
                    (state) => {
                        const {
                            customOrder,
                            mallContract,
                            thirdPeople,
                            personalDataCrossBorderTransfer,
                            preOrder,
                            familyPay,
                            booking,
                        } = state.selected
                        state.enabled =
                            customOrder &&
                            mallContract &&
                            thirdPeople &&
                            personalDataCrossBorderTransfer &&
                            preOrder &&
                            familyPay &&
                            booking
                    },
                )
        },
    })
